import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, ChevronUp, Handshake, Paperclip, Pencil, Plus, X } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Spinner,
  TBody,
  TD,
  TDNum,
  TH,
  THead,
  TR,
  Table,
} from '@pnl/ui';
import { PnlApiError } from '@pnl/sdk';
import type {
  AdminAffiliateItem,
  AdminWithdrawalStatus,
  AffiliateStatusKey,
  CreateAffiliateInput,
} from '@pnl/shared';
import { api } from '../lib/api';

function fmtBrl(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Formata CPF de 11 dígitos como XXX.XXX.XXX-XX; outros valores voltam como vieram. */
function fmtCpf(cpf: string): string {
  if (!/^\d{11}$/.test(cpf)) return cpf;
  return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`;
}

/** Extrai a mensagem de erro da API (agregando fields do zod quando presentes). */
function apiMsg(e: unknown, fallback: string): string {
  if (e instanceof PnlApiError) {
    return e.api.fields ? Object.values(e.api.fields).flat().join(' · ') : e.api.message;
  }
  return fallback;
}

/** Valida % inteiro 0-100 digitado; null = inválido (botão salvar desabilita). */
function parsePercent(v: string): number | null {
  if (v.trim() === '') return null;
  const n = Number(v.trim());
  return Number.isInteger(n) && n >= 0 && n <= 100 ? n : null;
}

const withdrawalTone: Record<
  AdminWithdrawalStatus,
  'neutral' | 'brand' | 'success' | 'warning' | 'danger'
> = {
  REQUESTED: 'warning',
  APPROVED: 'brand',
  PAID: 'success',
  REJECTED: 'danger',
  CANCELED: 'neutral',
};

const withdrawalLabel: Record<AdminWithdrawalStatus, string> = {
  REQUESTED: 'Em análise',
  APPROVED: 'Aprovado',
  PAID: 'Pago',
  REJECTED: 'Negado',
  CANCELED: 'Cancelado',
};

export function AffiliatesPage() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AdminAffiliateItem | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['admin', 'affiliates'],
    queryFn: () => api.listAffiliates(),
  });

  function refresh() {
    void qc.invalidateQueries({ queryKey: ['admin', 'affiliates'] });
  }

  const items = query.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">
            Operação
          </p>
          <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
            Afiliados
          </h1>
          <p className="text-ink-500 mt-1 text-sm">
            Parceiros com cupom exclusivo e comissão em dinheiro sobre as vendas. Cadastro manual;
            aprovar um saque debita o saldo do afiliado.
          </p>
        </div>
        <Button leadingIcon={<Plus size={14} />} onClick={() => setCreating(true)}>
          Novo afiliado
        </Button>
      </div>

      <Card className="overflow-hidden">
        {query.isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size={24} />
          </div>
        ) : query.isError ? (
          <div className="p-6">
            <Alert tone="danger">
              Não foi possível carregar os afiliados —{' '}
              {apiMsg(query.error, 'verifique a conexão e as permissões (affiliates:read).')}
            </Alert>
          </div>
        ) : items.length > 0 ? (
          <Table>
            <THead>
              <TR>
                <TH>Afiliado</TH>
                <TH>Cupom</TH>
                <TH className="text-right">% desconto</TH>
                <TH className="text-right">% comissão</TH>
                <TH className="text-right">Saldo</TH>
                <TH className="text-right">Vendas</TH>
                <TH>Status</TH>
                <TH className="text-right">Ações</TH>
              </TR>
            </THead>
            <TBody>
              {items.map((a) => (
                <Fragment key={a.id}>
                  <TR>
                    <TD>
                      <p className="text-ink-900 font-medium">{a.name}</p>
                      <p className="text-ink-500 text-xs">
                        {a.email}
                        {a.cpf ? ` · ${fmtCpf(a.cpf)}` : ''}
                      </p>
                    </TD>
                    <TD>
                      <span className="text-brand-700 font-mono font-semibold">{a.couponCode}</span>
                    </TD>
                    <TDNum>{a.discountValue}%</TDNum>
                    <TDNum>{a.commissionPercent}%</TDNum>
                    <TDNum>{fmtBrl(a.balanceCents)}</TDNum>
                    <TDNum>
                      {a.salesCount}
                      <span className="text-ink-400 block font-sans text-xs">
                        {fmtBrl(a.commissionTotalCents)} em comissões
                      </span>
                    </TDNum>
                    <TD>
                      <Badge tone={a.status === 'ACTIVE' ? 'success' : 'neutral'}>
                        {a.status === 'ACTIVE' ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TD>
                    <TD className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Editar ${a.name}`}
                          onClick={() => setEditing(a)}
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          trailingIcon={
                            expandedId === a.id ? (
                              <ChevronUp size={13} />
                            ) : (
                              <ChevronDown size={13} />
                            )
                          }
                          onClick={() => setExpandedId((cur) => (cur === a.id ? null : a.id))}
                        >
                          Detalhes
                        </Button>
                      </div>
                    </TD>
                  </TR>
                  {expandedId === a.id && (
                    <TR className="hover:bg-transparent">
                      <TD colSpan={8} className="bg-ink-50/40 px-4 py-4">
                        <AffiliateDetail affiliate={a} onBalanceChanged={refresh} />
                      </TD>
                    </TR>
                  )}
                </Fragment>
              ))}
            </TBody>
          </Table>
        ) : (
          <EmptyState
            icon={<Handshake size={32} />}
            title="Nenhum afiliado cadastrado"
            description="Afiliados divulgam um cupom exclusivo e ganham comissão em dinheiro por venda."
            action={<Button onClick={() => setCreating(true)}>Cadastrar primeiro afiliado</Button>}
          />
        )}
      </Card>

      {creating && (
        <AffiliateDrawer
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}

      {editing && (
        <EditAffiliateModal
          affiliate={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

/** Detalhe expandido do afiliado: vendas comissionadas + saques com auditoria. */
function AffiliateDetail({
  affiliate,
  onBalanceChanged,
}: {
  affiliate: AdminAffiliateItem;
  onBalanceChanged: () => void;
}) {
  const qc = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const sales = useQuery({
    queryKey: ['admin', 'affiliates', affiliate.id, 'sales'],
    queryFn: () => api.listAffiliateSales(affiliate.id),
  });
  const withdrawals = useQuery({
    queryKey: ['admin', 'affiliates', affiliate.id, 'withdrawals'],
    queryFn: () => api.listAffiliateWithdrawals(affiliate.id),
  });

  function refresh() {
    void qc.invalidateQueries({ queryKey: ['admin', 'affiliates', affiliate.id, 'withdrawals'] });
    // Aprovar debita o saldo — a lista principal também precisa atualizar.
    onBalanceChanged();
  }

  const approve = useMutation({
    mutationFn: (wid: string) => api.approveAffiliateWithdrawal(affiliate.id, wid),
    onSuccess: refresh,
    onError: (e) => setActionError(apiMsg(e, 'Falha ao aprovar o saque')),
  });
  const pay = useMutation({
    mutationFn: (wid: string) => api.markAffiliateWithdrawalPaid(affiliate.id, wid),
    onSuccess: refresh,
    onError: (e) => setActionError(apiMsg(e, 'Falha ao marcar como pago')),
  });
  const reject = useMutation({
    mutationFn: (wid: string) =>
      api.rejectAffiliateWithdrawal(affiliate.id, wid, { reason: reason.trim() }),
    onSuccess: () => {
      setRejectId(null);
      setReason('');
      refresh();
    },
    onError: (e) => setActionError(apiMsg(e, 'Falha ao rejeitar o saque')),
  });

  /** Baixa a nota fiscal (autenticada) e abre numa nova aba. */
  async function handleOpenInvoice(wid: string) {
    setActionError(null);
    try {
      const blob = await api.getAffiliateWithdrawalInvoiceBlob(affiliate.id, wid);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setActionError(apiMsg(e, 'Falha ao abrir a nota fiscal'));
    }
  }

  const salesItems = sales.data?.items ?? [];
  const withdrawalItems = withdrawals.data?.items ?? [];

  return (
    <div className="space-y-4">
      {actionError && <Alert tone="danger">{actionError}</Alert>}

      <div>
        <p className="text-2xs text-ink-500 font-semibold uppercase tracking-widest">
          Vendas comissionadas
        </p>
        {sales.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : salesItems.length === 0 ? (
          <p className="text-ink-500 py-4 text-sm">Nenhuma venda comissionada ainda.</p>
        ) : (
          <table className="mt-2 w-full text-sm">
            <thead>
              <tr className="text-ink-500 text-2xs uppercase tracking-wide">
                <th className="pb-2 text-left font-semibold">Data</th>
                <th className="pb-2 text-left font-semibold">Comprador</th>
                <th className="pb-2 text-left font-semibold">Plano</th>
                <th className="pb-2 text-right font-semibold">Valor pago</th>
                <th className="pb-2 text-right font-semibold">Comissão</th>
              </tr>
            </thead>
            <tbody className="divide-ink-100 divide-y">
              {salesItems.map((s) => (
                // Comissão revertida (reembolso da venda): valores riscados +
                // badge — a venda já está FORA dos totais/saldo vindos da API.
                <tr key={s.id} className={s.reversedAt ? 'opacity-75' : undefined}>
                  <td className="text-ink-600 py-2 text-xs">
                    {new Date(s.createdAt).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="text-ink-700 py-2 text-xs">{s.buyerEmail ?? '—'}</td>
                  <td className="text-ink-700 py-2 text-xs">{s.planName ?? '—'}</td>
                  <td
                    className={
                      'py-2 text-right tabular-nums ' +
                      (s.reversedAt ? 'text-ink-400 line-through' : 'text-ink-900')
                    }
                  >
                    {fmtBrl(s.saleAmountCents)}
                  </td>
                  <td className="py-2 text-right">
                    {s.reversedAt ? (
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-ink-400 font-semibold tabular-nums line-through">
                          {fmtBrl(s.commissionCents)}
                          <span className="ml-1 text-xs font-normal">({s.percentApplied}%)</span>
                        </span>
                        <Badge tone="danger">Revertida (reembolso)</Badge>
                      </div>
                    ) : (
                      <span className="text-success-700 font-semibold tabular-nums">
                        {fmtBrl(s.commissionCents)}
                        <span className="text-ink-400 ml-1 text-xs font-normal">
                          ({s.percentApplied}%)
                        </span>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div>
        <p className="text-2xs text-ink-500 font-semibold uppercase tracking-widest">Saques</p>
        {withdrawals.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : withdrawalItems.length === 0 ? (
          <p className="text-ink-500 py-4 text-sm">Nenhum saque solicitado ainda.</p>
        ) : (
          <table className="mt-2 w-full text-sm">
            <thead>
              <tr className="text-ink-500 text-2xs uppercase tracking-wide">
                <th className="pb-2 text-left font-semibold">Solicitado em</th>
                <th className="pb-2 text-right font-semibold">Valor</th>
                <th className="pb-2 text-left font-semibold">Pix</th>
                <th className="pb-2 text-left font-semibold">Nota fiscal</th>
                <th className="pb-2 text-left font-semibold">Status</th>
                <th className="pb-2 text-right font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-ink-100 divide-y">
              {withdrawalItems.map((w) => (
                <tr key={w.id}>
                  <td className="text-ink-600 py-2 text-xs">
                    {new Date(w.requestedAt).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="text-ink-900 py-2 text-right font-semibold tabular-nums">
                    {fmtBrl(w.amountCents)}
                  </td>
                  <td className="text-ink-600 py-2 text-xs">{w.pixKey}</td>
                  <td className="py-2">
                    {w.invoiceFileName ? (
                      <button
                        type="button"
                        onClick={() => void handleOpenInvoice(w.id)}
                        className="text-brand-600 hover:text-brand-700 inline-flex items-center gap-1 text-xs font-semibold"
                      >
                        <Paperclip size={11} /> Abrir NF
                      </button>
                    ) : (
                      <span className="text-ink-400 inline-flex items-center gap-1 text-xs">
                        <Paperclip size={11} /> Sem anexo
                      </span>
                    )}
                  </td>
                  <td className="py-2">
                    <Badge tone={withdrawalTone[w.status]}>{withdrawalLabel[w.status]}</Badge>
                    {w.status === 'REJECTED' && w.rejectionReason && (
                      <p className="text-ink-400 mt-0.5 text-xs">{w.rejectionReason}</p>
                    )}
                  </td>
                  <td className="py-2">
                    <div className="flex justify-end gap-2">
                      {w.status === 'REQUESTED' && (
                        <>
                          <Button
                            size="sm"
                            leadingIcon={<Check size={13} />}
                            loading={approve.isPending && approve.variables === w.id}
                            onClick={() => {
                              setActionError(null);
                              approve.mutate(w.id);
                            }}
                          >
                            Aprovar
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            leadingIcon={<X size={13} />}
                            onClick={() => {
                              setActionError(null);
                              setReason('');
                              setRejectId(w.id);
                            }}
                          >
                            Rejeitar
                          </Button>
                        </>
                      )}
                      {w.status === 'APPROVED' && (
                        <Button
                          size="sm"
                          loading={pay.isPending && pay.variables === w.id}
                          onClick={() => {
                            setActionError(null);
                            pay.mutate(w.id);
                          }}
                        >
                          Marcar pago
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {rejectId && (
        <div
          className="bg-ink-900/40 fixed inset-0 z-40 flex items-center justify-center p-4"
          role="presentation"
          onClick={() => setRejectId(null)}
        >
          <div
            className="border-ink-200 shadow-lift w-full max-w-md rounded-xl border bg-white p-6"
            role="dialog"
            aria-label="Rejeitar saque do afiliado"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-ink-900 text-lg font-semibold">Rejeitar saque</h2>
            <p className="text-ink-500 mt-1 text-sm">
              O afiliado vê o motivo no histórico. O saldo não é alterado.
            </p>
            <div className="mt-4">
              <Field label="Motivo" htmlFor="affiliate-reject-reason" required>
                <Input
                  id="affiliate-reject-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ex.: nota fiscal inválida / dados divergentes"
                />
              </Field>
            </div>
            <div className="mt-4 flex gap-2">
              <Button
                size="sm"
                variant="danger"
                loading={reject.isPending}
                onClick={() => reject.mutate(rejectId)}
              >
                Confirmar rejeição
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setRejectId(null)}>
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Drawer de cadastro manual (aprovação manual — só o admin cria afiliado). */
function AffiliateDrawer({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [cpf, setCpf] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [commissionPercent, setCommissionPercent] = useState('10');
  const [discountPercent, setDiscountPercent] = useState('50');
  const [error, setError] = useState<string | null>(null);

  const commissionParsed = parsePercent(commissionPercent);
  const discountParsed = parsePercent(discountPercent);

  const mutation = useMutation({
    mutationFn: () => {
      const input: CreateAffiliateInput = {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        cpf: cpf.trim(),
        couponCode: couponCode.trim().toUpperCase(),
        commissionPercent: commissionParsed ?? 10,
        couponDiscountPercent: discountParsed ?? 50,
      };
      return api.createAffiliate(input);
    },
    onSuccess: () => onSaved(),
    onError: (err) => setError(apiMsg(err, 'Erro de conexão.')),
  });

  return (
    <div
      /* Sem backdrop-blur: o blur sobre a página inteira brigava com a topbar
         fixa e deixava o topo com aspecto "borrado/quebrado". Overlay sólido
         translúcido, igual ao modal de rejeitar saque desta mesma tela. */
      className="bg-ink-900/50 animate-fade-in fixed inset-0 z-50 flex justify-end"
      onClick={onClose}
    >
      <div
        className="shadow-lift h-full w-full max-w-md overflow-y-auto bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-ink-200 border-b px-6 py-4">
          <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">
            Novo afiliado
          </p>
          <h2 className="font-display text-ink-900 mt-1 text-xl font-semibold">
            Cadastrar afiliado
          </h2>
          <p className="text-ink-500 mt-1 text-xs">
            O afiliado recebe um email para definir a senha e acessa a área de afiliados na
            plataforma do trader.
          </p>
        </div>

        <form
          className="space-y-4 p-6"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (commissionParsed === null || discountParsed === null) {
              setError('Percentuais devem ser inteiros entre 0 e 100.');
              return;
            }
            mutation.mutate();
          }}
        >
          {error && <Alert tone="danger">{error}</Alert>}

          <Field label="Nome" required>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
              maxLength={120}
            />
          </Field>

          <Field label="Email" required hint="Vira a conta de login do afiliado">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </Field>

          <Field label="CPF" required hint="Obrigatório — bloqueia a auto-compra com o cupom">
            <Input
              value={cpf}
              onChange={(e) => setCpf(e.target.value)}
              required
              minLength={11}
              maxLength={20}
              placeholder="000.000.000-00"
            />
          </Field>

          <Field label="Código do cupom" required hint="Letras, números, _ e -. Ex: PARCEIRO10">
            <Input
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              required
              minLength={3}
              maxLength={40}
              pattern="[A-Z0-9_\\-]+"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="% comissão" required hint="Sobre o valor pago">
              <Input
                type="number"
                value={commissionPercent}
                onChange={(e) => setCommissionPercent(e.target.value)}
                required
                min={0}
                max={100}
                step={1}
              />
            </Field>
            <Field label="% desconto" required hint="Do cupom, pro comprador">
              <Input
                type="number"
                value={discountPercent}
                onChange={(e) => setDiscountPercent(e.target.value)}
                required
                min={0}
                max={100}
                step={1}
              />
            </Field>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              Cadastrar afiliado
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Modal de edição: percentuais e status (o % de desconto atualiza o cupom). */
function EditAffiliateModal({
  affiliate,
  onClose,
  onSaved,
}: {
  affiliate: AdminAffiliateItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [commissionPercent, setCommissionPercent] = useState(String(affiliate.commissionPercent));
  const [discountPercent, setDiscountPercent] = useState(String(affiliate.discountValue));
  const [status, setStatus] = useState<AffiliateStatusKey>(affiliate.status);
  const [error, setError] = useState<string | null>(null);

  const commissionParsed = parsePercent(commissionPercent);
  const discountParsed = parsePercent(discountPercent);

  const mutation = useMutation({
    mutationFn: () =>
      api.updateAffiliate(affiliate.id, {
        commissionPercent: commissionParsed ?? affiliate.commissionPercent,
        couponDiscountPercent: discountParsed ?? affiliate.discountValue,
        status,
      }),
    onSuccess: () => onSaved(),
    onError: (err) => setError(apiMsg(err, 'Erro de conexão.')),
  });

  return (
    <div
      className="bg-ink-900/40 fixed inset-0 z-40 flex items-center justify-center p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="border-ink-200 shadow-lift w-full max-w-md rounded-xl border bg-white p-6"
        role="dialog"
        aria-label={`Editar afiliado ${affiliate.name}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-ink-900 text-lg font-semibold">Editar afiliado</h2>
        <p className="text-ink-500 mt-1 text-sm">
          {affiliate.name} · cupom{' '}
          <span className="text-brand-700 font-mono font-semibold">{affiliate.couponCode}</span>
        </p>

        <form
          className="mt-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (commissionParsed === null || discountParsed === null) {
              setError('Percentuais devem ser inteiros entre 0 e 100.');
              return;
            }
            mutation.mutate();
          }}
        >
          {error && <Alert tone="danger">{error}</Alert>}

          <div className="grid grid-cols-2 gap-3">
            <Field label="% comissão" required>
              <Input
                type="number"
                value={commissionPercent}
                onChange={(e) => setCommissionPercent(e.target.value)}
                required
                min={0}
                max={100}
                step={1}
              />
            </Field>
            <Field label="% desconto do cupom" required>
              <Input
                type="number"
                value={discountPercent}
                onChange={(e) => setDiscountPercent(e.target.value)}
                required
                min={0}
                max={99}
                step={1}
              />
            </Field>
          </div>

          <Field label="Status" hint="Inativo: o cupom para de funcionar no checkout">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as AffiliateStatusKey)}
              className="form-select border-ink-200 text-ink-900 shadow-soft focus:border-brand-500 focus:ring-brand-500/20 w-full rounded-lg border bg-white px-3 py-2.5 text-sm focus:ring-4"
            >
              <option value="ACTIVE">Ativo</option>
              <option value="INACTIVE">Inativo</option>
            </select>
          </Field>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              Salvar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
